import React from "react";
import { useDraft } from "../../lib/draft-store";

const OCCASIONS = [
  {
    key: "wedding",
    name: "Wedding",
    sub: "Full-day Indian wedding",
    img:
      "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "pre_wedding",
    name: "Pre-wedding shoot",
    sub: "Outdoor couple stories",
    img:
      "https://images.unsplash.com/photo-1503516459261-40c66117780a?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "engagement",
    name: "Engagement / Proposal",
    sub: "Moments that started it all",
    img:
      "https://images.unsplash.com/photo-1591604466107-ec97de577aff?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "birthday",
    name: "Birthday",
    sub: "Milestone celebrations",
    img:
      "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "anniversary",
    name: "Anniversary",
    sub: "Quiet, lasting tributes",
    img:
      "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "maternity",
    name: "Maternity portraits",
    sub: "Soft, considered, you",
    img:
      "https://images.unsplash.com/photo-1519689680058-324335c77eba?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "newborn",
    name: "Newborn",
    sub: "Tender first frames",
    img:
      "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?auto=format&fit=crop&w=900&q=70",
  },
  {
    key: "other",
    name: "Other celebration",
    sub: "Tell us what's special",
    img:
      "https://images.unsplash.com/photo-1532712938310-34cb3982ef74?auto=format&fit=crop&w=900&q=70",
  },
];

const WEDDING_SUBTYPES = [
  { key: "traditional", label: "Traditional ceremony" },
  { key: "reception", label: "Reception" },
  { key: "sangeet", label: "Sangeet" },
  { key: "haldi", label: "Haldi" },
  { key: "mehendi", label: "Mehendi" },
  { key: "pre_wedding_shoot", label: "Pre-wedding shoot" },
  { key: "post_wedding", label: "Post-wedding portraits" },
  { key: "multi_day", label: "Complete multi-day wedding" },
];

export default function Step1Occasion() {
  const { payload, setField } = useDraft();
  const selected = payload?.occasion || null;
  const subs = payload?.occasion_subtypes || [];

  const pick = (key) => {
    if (selected !== key) {
      // Clear scope answers when occasion changes — different question set
      setField("scope", {});
    }
    setField("occasion", key);
    if (key !== "wedding") setField("occasion_subtypes", []);
  };

  const toggleSub = (key) => {
    const next = subs.includes(key) ? subs.filter((s) => s !== key) : [...subs, key];
    setField("occasion_subtypes", next);
  };

  return (
    <div data-testid="plan-step-1">
      <h1 className="font-display text-3xl text-[#1A1A1A] sm:text-4xl">
        What are we shooting?
      </h1>
      <p className="mt-3 max-w-xl text-base text-[#1A1A1A]/65">
        Pick the closest match — you’ll add the details next.
      </p>

      <ul
        data-testid="occasion-grid"
        className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3"
      >
        {OCCASIONS.map((o) => {
          const active = selected === o.key;
          return (
            <li key={o.key}>
              <button
                type="button"
                data-testid={`occasion-tile-${o.key}`}
                onClick={() => pick(o.key)}
                aria-pressed={active}
                className={[
                  "group relative block aspect-[4/5] w-full overflow-hidden rounded-xl border text-left transition",
                  active
                    ? "border-[#C8A24B] ring-2 ring-[#C8A24B]/40"
                    : "border-[#EAE3D2] hover:border-[#C8A24B]",
                ].join(" ")}
              >
                <div
                  className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#2a2622] to-[#C8A24B]"
                  aria-hidden="true"
                />
                <img
                  src={o.img}
                  alt={o.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    // Hide the broken image so the gradient placeholder behind shows through.
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/85 via-[#1A1A1A]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 text-[#FAF7F2]">
                  <p className="font-display text-lg sm:text-xl">{o.name}</p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-[#FAF7F2]/70">
                    {o.sub}
                  </p>
                </div>
                {active ? (
                  <span className="absolute right-3 top-3 rounded-full bg-[#C8A24B] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[#1A1A1A]">
                    Selected
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>

      {selected === "wedding" ? (
        <div
          data-testid="wedding-subtypes"
          className="mt-10 rounded-xl border border-[#EAE3D2] bg-white p-5"
        >
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
            Which events?
          </p>
          <p className="mt-1 font-display text-lg text-[#1A1A1A]">
            Pick all that apply
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {WEDDING_SUBTYPES.map((s) => {
              const active = subs.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`wedding-subtype-${s.key}`}
                  onClick={() => toggleSub(s.key)}
                  aria-pressed={active}
                  className={[
                    "rounded-full border px-4 py-2 text-xs uppercase tracking-[0.16em] transition",
                    active
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
                      : "border-[#1A1A1A]/15 text-[#1A1A1A]/80 hover:border-[#C8A24B] hover:text-[#C8A24B]",
                  ].join(" ")}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function step1IsValid(payload) {
  return Boolean(payload?.occasion);
}
